import axios from 'axios';
import API from '../../../../config/API';
import { newServices } from '../../../../_services/newservices.service';
import { hairdresserService } from '../../../../_services/hairdresser.service';
import { STARTER_MENUS, starterRows } from '../../starterMenus';

/**
 * "Your menu": the catalogue calls, kept apart from the screens so the rules
 * live in one place.
 *
 *  - Always the raw services, never the redux twins. Those replace the list
 *    an open catalogue page renders, and any error wipes it.
 *  - commonServiceId is the service, hairdresserId is the person.
 *  - Every service carries a row for every team member, switched off for the
 *    people who don't do it, so putting someone on later is just a flip.
 *  - An edit re-reads the service first and sends the whole row set back,
 *    exactly as the service editor does (ServiceEditorPanel handleSave).
 *  - Creating is safe to repeat: names already on the menu are skipped and
 *    categories are reused by name, the way starterSetup does it.
 *  - The owner's list also carries self-employed stylists' own services
 *    (shared with the salon, in their own categories). Those are theirs:
 *    setup's bulk actions only ever touch the salon's own (see ownsCategory).
 */

export const DEFAULT_COLOUR = '#c8eeee';
export const PATCH_NAME = 'Patch Test';
export const PATCH_CATEGORY = 'Consultations';
export const PATCH_MINUTES = 15;
export const PATCH_MESSAGE = 'New clients need a quick patch test before their first colour. Please book one first.';
export const ROOMS_NAME = 'Treatment rooms';

export const norm = (s) => String(s || '').trim().toLowerCase();
export const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
export const round5 = (v) => Math.max(5, Math.round(num(v) / 5) * 5);
export const round2 = (v) => Math.round(num(v) * 100) / 100;
export const uniq = (list) => Array.from(new Set(list));
// Ids can arrive as strings or populated records.
export const idOf = (x) => String((x && typeof x === 'object' ? x._id : x) || '');

const ok = (res) => !!(res && res.data && res.data.statusCode === 1);
// A list or a view that came back as a refusal rather than data.
const refused = (res) => !res || !res.data || (res.data.statusCode !== undefined && res.data.statusCode !== 1);
const result = (res) => (res && res.data && res.data.responseData && res.data.responseData.result) || null;

/** Reasons from the server reach the owner, so they follow the copy rules too. */
export const clean = (text) => String(text || '')
  .replace(/\s*[\u2014\u2013]\s*([a-z])?/g, (m, c) => `. ${c ? c.toUpperCase() : ''}`)
  .trim();
const reason = (res, fallback) => clean((res && res.data && res.data.error && (res.data.error.responseMessage || res.data.error.message)) || fallback);

/** An open catalogue page reloads when this fires (NewServices.js). */
export function catalogueChanged() {
  try { window.dispatchEvent(new CustomEvent('beu:catalogue-changed')); } catch (e) { /* ignore */ }
}

/* ---------------- the team ---------------- */

/**
 * Everyone who can hold a service row. The Team page's own list gives types
 * and photos; the service list's people are folded into rowIds so nobody who
 * can hold a row misses one. The signed-in owner is always there, as in
 * starterSetup. `typed` is false when only the service list answered, so
 * nobody's employed or self-employed status is known.
 */
export async function loadTeam(myName) {
  const me = String(localStorage.getItem('userId') || '');
  const [rota, list] = await Promise.all([
    hairdresserService.staffRotaV2({ hairdresserId: me, searchKey: '', page: 1, listType: 1 }).catch(() => null),
    newServices.listHairdresser({}).catch(() => null),
  ]);
  const rotaOk = ok(rota);
  const listOk = !refused(list);
  if (!rotaOk && !listOk) throw new Error('I couldn’t read your team just now.');

  const people = [];
  const add = (p) => { if (p.id && !people.some((x) => x.id === p.id)) people.push(p); };
  const rotaRows = rotaOk ? ((rota.data.responseData && rota.data.responseData.result) || []) : [];
  const listRows = listOk ? (result(list) || []).filter((x) => x && x._id) : [];
  // The service list is who the catalogue can hold a row for. Someone on the
  // team page but not on it would have their row dropped when it's saved.
  const listIds = new Set(listRows.map((x) => idOf(x._id)));
  rotaRows.filter(Boolean).filter((r) => r.name !== 'Unknown' && r.email !== 'Unknown').forEach((r) => {
    const info = r.hairdresserInfo || {};
    const id = idOf(r._id);
    const owner = id === me || Number(info.isSalonOwner) === 1;
    add({
      id,
      name: String(r.name || '').trim() || 'Team member',
      photo: (r.userImage && r.userImage.circleImage) || '',
      me: id === me,
      owner,
      se: !owner && Number(info.employmentTypeId) === 2,
      holds: !listOk || !listIds.size || id === me || listIds.has(id),
      // Server's own answer (hairdresser-staff canHoldServices); undefined on
      // an older server, then `holds` alone decides.
      canHold: info.canHoldServices === false ? false : undefined,
      heldBackBecause: info.servicesBlockedReason || null,
    });
  });
  if (!rotaOk) {
    listRows.forEach((x) => add({ id: idOf(x._id), name: String(x.name || '').trim() || 'Team member', photo: '', me: idOf(x._id) === me, owner: idOf(x._id) === me, se: false }));
  }
  if (me && !people.some((p) => p.id === me)) people.unshift({ id: me, name: myName || 'You', photo: '', me: true, owner: true, se: false });
  // Rows go only to people the salon's catalogue can hold (the same list
  // service/view reads back). A self-employed stylist who keeps their own
  // menu (shareService 0) or an unaccepted invite must never get a row: the
  // server refuses the whole save for them, and before it did, the rows were
  // written invisibly onto the stylist's private menu. The team list's
  // canHoldServices flag is the source of truth when the server sends it.
  const canHold = (p) => p.holds !== false && p.canHold !== false;
  return { people, rowIds: uniq(people.filter(canHold).map((p) => p.id).concat(listRows.map((x) => idOf(x._id)))), typed: rotaOk };
}

/* ---------------- the catalogue ---------------- */

/**
 * Which categories are the salon's own, from the team as loadTeam reads it.
 * A category belongs to whoever made it (its hairdresserId): the salon's
 * are the signed-in owner's, the owner's or an employed team member's (a
 * manager with the catalogue permission), and legacy ones with no maker.
 * A self-employed stylist's own category, anyone no longer on the team,
 * and anyone whose type couldn't be read are not the salon's.
 */
export function ownsCategory(team) {
  const me = String(localStorage.getItem('userId') || '');
  const typed = !!team && team.typed !== false;
  const salonSide = new Set(((team && team.people) || []).filter((p) => p.me || p.owner || (typed && !p.se)).map((p) => p.id));
  return (c) => !!c && c.known !== false && (!c.ownerId || c.ownerId === me || salonSide.has(c.ownerId));
}

/**
 * Categories (empty ones too) and services grouped as the catalogue shows
 * them. The list carries one person's values plus a range, never a row per
 * person, and a service can repeat, so it's deduped by commonServiceId.
 *
 * Each category carries who made it (ownerId). A group the service list
 * shows but the category list doesn't know (a stylist's private category
 * that holds a row for the owner) is marked known: false. With `owns` (from
 * ownsCategory), every category, group and service also says whether it's
 * the salon's own (`own`).
 */
export async function loadCatalogue(owns) {
  const [cats, svcs] = await Promise.all([
    newServices.listCategory({}),
    newServices.listServices({ hairdresserId: '', onlineBooking: '', status: '', search: '' }),
  ]);
  if (refused(svcs)) throw new Error(reason(svcs, 'I couldn’t read your services just now.'));
  // Without the categories nobody can tell whose a service is, so stop here.
  if (refused(cats)) throw new Error(reason(cats, 'I couldn’t read your categories just now.'));
  const categories = (result(cats) || [])
    .filter(Boolean)
    .map((c) => ({ id: idOf(c._id), name: String(c.categoryName || '').trim(), ownerId: c.hairdresserId ? idOf(c.hairdresserId) : '' }))
    .filter((c) => c.id && c.name);
  const byId = categories.reduce((o, c) => ({ ...o, [c.id]: c }), {});
  const seen = new Set();
  const groups = [];
  const list = result(svcs);
  (Array.isArray(list) ? list : []).filter((c) => c && !c.isFeaturedCategory).forEach((c) => {
    const id = idOf(c._id);
    const known = byId[id];
    const group = {
      id,
      name: String(c.categoryName || '').trim() || 'Other',
      ownerId: known ? known.ownerId : '',
      known: !!known,
      services: [],
    };
    (c.serviceList || []).forEach((s) => {
      const sid = idOf(s.commonServiceId || s._id);
      if (!sid || seen.has(sid)) return;
      seen.add(sid);
      group.services.push({
        id: sid,
        name: String(s.serviceName || '').trim(),
        categoryId: group.id,
        category: group.name,
        price: num(s.servicePrice),
        fromPrice: s.fromPrice != null ? num(s.fromPrice) : null,
        time: num(s.serviceTime),
        fromTime: s.fromDuration != null ? num(s.fromDuration) : null,
        notAssigned: !!s.notAssigned,
      });
    });
    if (group.services.length) groups.push(group);
  });
  const cat = { categories, groups, services: groups.reduce((all, g) => all.concat(g.services), []) };
  return owns ? markOwn(cat, owns) : cat;
}

/** The same catalogue with `own` on every category, group and service (see ownsCategory). */
export function markOwn(cat, owns) {
  if (!cat || !owns) return cat;
  const groups = cat.groups.map((g) => {
    const own = owns(g);
    return { ...g, own, services: g.services.map((x) => ({ ...x, own })) };
  });
  return {
    categories: cat.categories.map((c) => ({ ...c, own: owns(c) })),
    groups,
    services: groups.reduce((all, g) => all.concat(g.services), []),
  };
}

/**
 * The salon's own part of a catalogue loaded with `owns`: its categories,
 * groups and services, plus `theirs`, the services left out because they
 * belong to someone else's menu.
 */
export function ownPart(cat) {
  if (!cat) return cat;
  const groups = cat.groups.filter((g) => g.own !== false);
  const services = groups.reduce((all, g) => all.concat(g.services), []);
  return {
    categories: cat.categories.filter((c) => c.own !== false),
    groups,
    services,
    theirs: cat.services.filter((x) => x.own === false),
  };
}

/* ---------------- one service, row by row ---------------- */

/** Every person's row for one service. The only call that has them all. */
export async function fetchView(id) {
  const res = await newServices.viewService({ commonServiceId: id });
  const v = result(res);
  if (refused(res) || !v || typeof v !== 'object') throw new Error(reason(res, 'I couldn’t open this service just now.'));
  return v;
}

/** A row as the service editor sends it back, from a row the server sent. */
export function toRow(r, base, req) {
  const colour = r.diaryColour || base;
  return {
    hairdresserId: idOf(r.hairdresserId),
    servicePrice: num(r.servicePrice),
    serviceTime: num(r.serviceTime),
    isDeactivated: !!r.isDeactivated,
    depositPrice: num(r.depositPrice),
    onlineBooking: !!r.onlineBooking,
    processingTime: num(r.processingTime),
    finishingTime: num(r.finishingTime),
    setupTime: num(r.setupTime),
    cleanupTime: num(r.cleanupTime),
    requiresFinishing: !!r.requiresFinishing,
    finishingServiceIds: r.finishingServiceIds || [],
    finishingSameStylist: r.finishingSameStylist !== false,
    requiresConsultation: !!r.requiresConsultation,
    consultationServiceId: r.consultationServiceId || null,
    consultationSameStylist: r.consultationSameStylist !== false,
    finishingMessage: r.finishingMessage || '',
    consultationMessage: r.consultationMessage || '',
    diaryColour: colour,
    isColourOverridden: colour !== base,
    linkedForms: (r.linkedForms || []).map((f) => ({ formId: f.formId, formName: f.formName, isRequired: f.isRequired !== false })),
    resourceRequirements: req,
  };
}

/** The whole row set, with the service-wide room rule stamped on every row. */
export function rowsOf(v) {
  const base = (v && v.diaryColour) || DEFAULT_COLOUR;
  const list = ((v && v.hairdresserService) || []).filter((r) => r && r.hairdresserId);
  const first = list.find((r) => Array.isArray(r.resourceRequirements) && r.resourceRequirements.length);
  const req = first
    ? first.resourceRequirements.map((x) => (x.resourceId ? { groupId: idOf(x.groupId), resourceId: idOf(x.resourceId) } : { groupId: idOf(x.groupId) }))
    : [];
  return list.map((r) => toRow(r, base, req));
}

/** A fresh row for one person. */
export function newRow(hairdresserId, { price = 0, time = 30, on = false } = {}) {
  return {
    hairdresserId: String(hairdresserId),
    servicePrice: round2(price),
    serviceTime: round5(time),
    isDeactivated: !on,
    depositPrice: 0,
    onlineBooking: !!on,
    processingTime: 0,
    finishingTime: 0,
    setupTime: 0,
    cleanupTime: 0,
    requiresFinishing: false,
    finishingServiceIds: [],
    finishingSameStylist: true,
    requiresConsultation: false,
    consultationServiceId: null,
    consultationSameStylist: true,
    finishingMessage: '',
    consultationMessage: '',
    diaryColour: DEFAULT_COLOUR,
    isColourOverridden: false,
    linkedForms: [],
    resourceRequirements: [],
  };
}

const SHARED = ['processingTime', 'finishingTime', 'setupTime', 'cleanupTime', 'requiresFinishing', 'finishingServiceIds', 'finishingSameStylist',
  'requiresConsultation', 'consultationServiceId', 'consultationSameStylist', 'finishingMessage', 'consultationMessage', 'linkedForms',
  'resourceRequirements', 'diaryColour', 'isColourOverridden'];

/**
 * A row for someone the service has no record for yet (a person who joined
 * after it was made). It copies the service's rules from a working row, so
 * a patch test, a room or developing time carries over. Off until switched on.
 */
export function newRowFrom(rows, pid, std) {
  const model = rows.find((r) => !r.isDeactivated) || rows[0] || null;
  const row = newRow(pid, { price: std.price, time: std.time, on: false });
  if (model) SHARED.forEach((k) => { row[k] = Array.isArray(model[k]) ? model[k].slice() : model[k]; });
  if (model) row.depositPrice = Math.min(num(model.depositPrice), row.servicePrice);
  return row;
}

const byCount = (values) => {
  const counts = new Map();
  values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  let best = null;
  let most = 0;
  counts.forEach((c, v) => { if (c > most) { best = v; most = c; } });
  return best;
};
export const modeOf = byCount;
export const totalOf = (r) => num(r.serviceTime) + num(r.processingTime) + num(r.finishingTime);

/** The service's usual price and time: what most people doing it charge. */
export function standardOf(rows, fallback = {}) {
  const on = rows.filter((r) => !r.isDeactivated);
  const pick = (key) => byCount(on.map((r) => num(r[key])).filter((x) => x > 0)) || byCount(rows.map((r) => num(r[key])).filter((x) => x > 0));
  return {
    price: pick('servicePrice') || num(fallback.price) || 0,
    time: pick('serviceTime') || num(fallback.time) || 30,
  };
}

/** The editor's own checks, applied quietly before anything is sent. */
export function tidyRows(rows) {
  return rows.map((r) => {
    const x = { ...r };
    x.servicePrice = Math.max(0, round2(x.servicePrice));
    if (!x.isDeactivated) x.serviceTime = round5(x.serviceTime || 30);
    else if (num(x.serviceTime) > 0) x.serviceTime = round5(x.serviceTime);
    x.processingTime = num(x.processingTime) > 0 ? round5(x.processingTime) : 0;
    x.finishingTime = num(x.finishingTime) > 0 ? round5(x.finishingTime) : 0;
    x.depositPrice = Math.min(Math.max(0, round2(x.depositPrice)), x.servicePrice);
    return x;
  });
}

/** Sends the whole row set back with the service's own fields, as read. */
export async function saveRows(id, v, rows, serviceName) {
  const cat = v.categoryID || (v.categoryId && typeof v.categoryId === 'object' ? v.categoryId._id : v.categoryId) || '';
  const res = await newServices.editServices({
    commonServiceId: id,
    serviceName: String(serviceName || v.serviceName || '').trim(),
    categoryID: String(cat || ''),
    serviceDescription: v.serviceDescription || '',
    diaryColour: v.diaryColour || DEFAULT_COLOUR,
    hairdresserService: rows,
  });
  if (!ok(res)) throw new Error(reason(res, 'That didn’t save. Try again.'));
}

/* ---------------- creating ---------------- */

const createdId = (res) => {
  const x = result(res);
  const one = Array.isArray(x) ? x[0] : x;
  return idOf(one && one.commonServiceId);
};

/**
 * The category to put a new service in: the salon's own one with that name,
 * or a new one. A self-employed stylist's category with the same name is
 * never used, since a salon service in it would come and go with their
 * sharing. `known` maps a lower-cased name to an id and is filled as it goes.
 */
async function ensureCategory(name, known) {
  const label = String(name || '').trim() || 'Services';
  if (known[norm(label)]) return known[norm(label)];
  const res = await newServices.createCategory({ categoryName: label });
  if (!ok(res)) throw new Error(reason(res, `I couldn’t add the ${label} category.`));
  const c = result(res);
  const id = idOf(c && (c._id || (Array.isArray(c) && c[0] && c[0]._id)));
  if (!id) throw new Error(`I couldn’t add the ${label} category.`);
  known[norm(label)] = id;
  return id;
}

// Adds run one after another, and each reads the menu afresh in its turn,
// so two quick taps can never both pass the "already on the menu" check.
let turn = Promise.resolve();
let waiting = 0;
/** True while services are being added (or waiting to be), even after the screen has gone. */
export const isCreating = () => waiting > 0;

async function addServices(rows, { rowIds, onFor, onProgress, onCreated, owns }) {
  const picked = rows.filter((r) => r.on !== false && String(r.name || '').trim());
  if (!picked.length) return { created: [], skipped: 0 };
  // Only the salon's own menu counts: its categories are reused by name,
  // and a name is "already on the menu" only when the salon has it.
  const mine = owns || ownsCategory(null);
  const cat = ownPart(await loadCatalogue(mine));
  const known = {};
  cat.categories.forEach((c) => { if (!known[norm(c.name)]) known[norm(c.name)] = c.id; });
  const names = new Set(cat.services.map((s) => norm(s.name)));
  const created = [];
  let skipped = 0;
  for (let i = 0; i < picked.length; i += 1) {
    const row = picked[i];
    const name = String(row.name).trim();
    if (onProgress) onProgress(i, picked.length);
    if (names.has(norm(name))) {
      skipped += 1;
    } else {
      const categoryID = await ensureCategory(row.category, known);
      const price = round2(row.price);
      const time = round5(row.time || 30);
      const res = await newServices.createServices({
        serviceName: name,
        categoryID,
        serviceDescription: '',
        diaryColour: DEFAULT_COLOUR,
        hairdresserService: rowIds.map((pid) => newRow(pid, { price, time, on: !!onFor(pid) })),
      });
      if (!ok(res)) throw new Error(reason(res, `I couldn’t add ${name}.`));
      names.add(norm(name));
      const id = createdId(res);
      created.push({ id, name });
      if (id && onCreated) onCreated(id);
    }
  }
  // The create reply doesn't always carry the id, so look it up by name.
  if (created.some((c) => !c.id)) {
    const after = await loadCatalogue(mine).then(ownPart).catch(() => null);
    if (after) {
      created.forEach((c) => {
        if (c.id) return;
        const hit = after.services.find((s) => norm(s.name) === norm(c.name));
        if (hit) { c.id = hit.id; if (onCreated) onCreated(hit.id); }
      });
    }
  }
  if (onProgress) onProgress(picked.length, picked.length);
  return { created, skipped };
}

/**
 * Adds each row as a service, with a row for every person: on (and bookable
 * online) for the people onFor() picks, off for everyone else. Names the
 * menu already has are skipped. Rows: { name, category, time, price }.
 * Pass `owns` (ownsCategory) so only the salon's own menu counts; without
 * it nobody's type is known and only the signed-in owner's categories do.
 */
export function createMenu(rows, opts) {
  waiting += 1;
  const run = () => addServices(rows, opts);
  const p = turn.then(run, run);
  turn = p.catch(() => {});
  return p.then((out) => { waiting -= 1; return out; }, (e) => { waiting -= 1; throw e; });
}

/* ---------------- starter menus ---------------- */

/** One starter menu from several trades, with each service once. */
export function starterFor(trades, { withPrices }) {
  const seen = new Set();
  const out = [];
  (trades || []).filter((t) => STARTER_MENUS[t]).forEach((t) => {
    starterRows(t, { withPrices }).forEach((r) => {
      if (seen.has(norm(r.name))) return;
      seen.add(norm(r.name));
      out.push(r);
    });
  });
  return out;
}

/* ---------------- colour ---------------- */

// The same test the ready check uses (readyCheck.js colourTargets), so what
// it flags is exactly what this chapter offers to protect: colour, tint,
// bleach, toner, balayage, highlights and any lash service. Consultations
// and patch tests are the protection, so they never count.
const COLOUR_RE = /\b(colou?r(s|ing)?|tints?|tinting|lash(es)?|bleach(ing)?|highlights?|lowlights?|balayage|babylights|ombr[eé]|toner?|foils?|dye)\b/i;
const GUARD_RE = /consult|patch|skin test|allergy test/i;
const LASH_BROW_RE = /\b(lash|lashes|eyelash|eyelashes|brow|brows|eyebrow|eyebrows)\b/i;

export const isConsultation = (name) => GUARD_RE.test(String(name || ''));
/** Colour, tint, bleach, toner, balayage, highlights and lash services. */
export const needsPatchTest = (name) => COLOUR_RE.test(String(name || '')) && !isConsultation(name);
/** Hair colour that sits and develops (not a lash or brow service). */
export const developsColour = (name) => needsPatchTest(name) && !LASH_BROW_RE.test(String(name || ''));

/**
 * Reuses a patch test already on the salon's own menu, or adds a free one.
 * A self-employed stylist's own patch test is never borrowed.
 */
export async function ensurePatchTest({ rowIds, onFor, owns }) {
  const cat = ownPart(await loadCatalogue(owns || ownsCategory(null)));
  const found = cat.services.find((s) => /\b(patch|skin|allergy)\s+test/i.test(s.name));
  if (found) return { id: found.id, name: found.name, created: false };
  const { created } = await createMenu([{ name: PATCH_NAME, category: PATCH_CATEGORY, time: PATCH_MINUTES, price: 0 }], { rowIds, onFor, owns });
  if (!created.length || !created[0].id) throw new Error('I couldn’t add the patch test. Try again.');
  return { id: created[0].id, name: PATCH_NAME, created: true };
}

/** A patch test nobody does can't be booked, so the colour team goes on it. */
export function switchOnIfNobody(rows, onFor) {
  if (rows.some((r) => !r.isDeactivated)) return rows;
  return rows.map((r) => (onFor(r.hairdresserId)
    ? { ...r, isDeactivated: false, onlineBooking: true, serviceTime: r.serviceTime > 0 ? r.serviceTime : PATCH_MINUTES }
    : r));
}

/**
 * A starting split of one colour appointment: apply, develop, finish. The
 * total stays the same, and developing time sits between 15 and 45 minutes.
 */
export function suggestSplit(total) {
  const t = Math.max(30, round5(total));
  const develop = Math.min(45, Math.max(15, round5(t * 0.3)));
  const apply = Math.max(10, round5((t - develop) * 0.4));
  return { apply, develop, finish: Math.max(5, t - develop - apply) };
}

/**
 * Splits every row. Rows at the service's usual length take the split as it
 * is. Someone with their own length keeps it: the same developing and
 * finishing time, with the rest as their apply time.
 */
export function splitRows(rows, { apply, develop, finish }) {
  const usual = byCount(rows.filter((r) => !r.isDeactivated).map(totalOf).filter((x) => x > 0));
  return rows.map((r) => {
    const t = totalOf(r);
    if (!t || !usual || t === usual) return { ...r, serviceTime: apply, processingTime: develop, finishingTime: finish };
    const own = t - develop - finish;
    return own >= 5 ? { ...r, serviceTime: round5(own), processingTime: develop, finishingTime: finish } : r;
  });
}

/** Back to one block of time, the same length as before. */
export function joinRows(rows) {
  return rows.map((r) => {
    const t = totalOf(r);
    return { ...r, serviceTime: t > 0 ? round5(t) : r.serviceTime, processingTime: 0, finishingTime: 0 };
  });
}

export const depositFor = (price, pct) => round2((num(price) * num(pct)) / 100);

/* ---------------- deposits ---------------- */

/**
 * The rows a salon-wide deposit may go on. On one salon menu, everyone's.
 * Otherwise a self-employed stylist's deposits are theirs to set, so only
 * the owner's and the employed team's; and when nobody's type could be
 * read, only the signed-in owner's.
 */
export function depositWriter(team, menuModel) {
  if (menuModel === 'salon') return () => true;
  const people = (team && team.people) || [];
  const ids = new Set(people.filter((p) => (team.typed === false ? p.me : !p.se)).map((p) => p.id));
  return (pid) => ids.has(idOf(pid));
}

/**
 * Deposits chosen before card payments were live are held in setup
 * (answers.menu.depositPlan), not written, so no client is asked to pay
 * before Beu can take the money:
 *   { mode: 'percent', value: 20, at }                   a share of every price
 *   { mode: 'amount', value: { [serviceId]: 10 }, at }   set amounts, service by service
 */
const covers = (plan, id) => !!plan && (plan.mode === 'percent' || Object.prototype.hasOwnProperty.call(plan.value || {}, id));

/** A plan's deposit for one service at `price`, or null when it doesn't cover the service. */
export function plannedDeposit(plan, id, price) {
  if (!covers(plan, id)) return null;
  if (plan.mode === 'percent') return depositFor(price, plan.value);
  return Math.min(num(price), num(plan.value[id]));
}

/** One service's rows with a plan's deposit on the rows it may go on. Every deposit write goes through here. */
export function withPlanDeposit(rows, plan, id, writes) {
  return rows.map((r) => {
    if (!writes(r.hairdresserId)) return r;
    const dep = plan.mode === 'percent' ? depositFor(r.servicePrice, plan.value) : Math.min(num(r.servicePrice), num((plan.value || {})[id]));
    return { ...r, depositPrice: dep };
  });
}

/**
 * Switches a held deposit plan on, now card payments are live. Each service
 * is re-read and sent back whole, as the menu screen saves it, three at a
 * time. Safe to run again: a service that already matches is left alone.
 * Throws a plain message when some didn't save; those that did stay saved.
 */
export async function applyDepositPlan(plan, { menuModel } = {}) {
  if (!plan || !['percent', 'amount'].includes(plan.mode)) return { done: 0 };
  let team;
  let cat;
  try {
    team = await loadTeam();
    // The salon's own services only: a self-employed stylist's own ones
    // (shared into the owner's list) are theirs to set.
    cat = ownPart(await loadCatalogue(ownsCategory(team)));
  } catch (e) {
    throw new Error('I couldn’t read your menu just now, so your deposits aren’t on yet. Try again.');
  }
  const writes = depositWriter(team, menuModel);
  const todo = cat.services.filter((s) => !isConsultation(s.name) && covers(plan, s.id));
  let failed = 0;
  const one = async (s) => {
    const v = await fetchView(s.id);
    const rows = rowsOf(v);
    const next = tidyRows(withPlanDeposit(rows, plan, s.id, writes));
    if (next.some((r, i) => r.depositPrice !== num(rows[i].depositPrice))) await saveRows(s.id, v, next);
  };
  const worker = async () => {
    while (todo.length) {
      const s = todo.shift();
      try { await one(s); } catch (e) { failed += 1; }
    }
  };
  const total = todo.length;
  await Promise.all([worker(), worker(), worker()]);
  if (total) catalogueChanged();
  if (failed) throw new Error(`${failed === 1 ? 'One service' : `${failed} services`} didn’t take the deposit just now. Try again, and I’ll finish the rest.`);
  return { done: total };
}

/* ---------------- rooms (Catalogue > Resources) ---------------- */

const jsonHeaders = () => ({ 'Content-Type': 'application/json', accessToken: localStorage.getItem('accessToken') });

/**
 * The salon's resource groups and units. `units` are the active ones (what
 * the diary books); `inactive` are units switched off in Resources, kept so
 * adding rooms can switch one back on rather than make a second with the
 * same name.
 */
export async function loadRooms() {
  const res = await axios.get(API.RESOURCE_LIST, { headers: jsonHeaders() });
  const d = (res && res.data && res.data.responseData) || {};
  const all = (d.resources || []).filter(Boolean).map((u) => ({ id: idOf(u._id), groupId: idOf(u.groupId), name: String(u.name || ''), active: u.isActive !== false }));
  return {
    groups: (d.groups || []).filter(Boolean).map((g) => ({ id: idOf(g._id), name: String(g.name || '') })),
    units: all.filter((u) => u.active),
    inactive: all.filter((u) => !u.active),
  };
}

/** The "Treatment rooms" group, found by name. Never any other group. */
export const roomsGroupOf = (groups) => (groups || []).find((g) => norm(g.name) === norm(ROOMS_NAME)) || null;

/**
 * Every row with the rooms group needed (on) or not (off). Only that
 * group's entry is added or taken out: other groups' entries, and a room
 * pinned within this group, stay exactly as they were.
 */
export function withRoomGroup(rows, groupId, on) {
  const gid = idOf(groupId);
  return rows.map((r) => {
    const req = (Array.isArray(r.resourceRequirements) ? r.resourceRequirements : []).filter((q) => q && q.groupId);
    const has = req.some((q) => idOf(q.groupId) === gid);
    let next = req;
    if (on && !has) next = req.concat({ groupId: gid });
    if (!on && has) next = req.filter((q) => idOf(q.groupId) !== gid);
    return { ...r, resourceRequirements: next.map((q) => ({ ...q })) };
  });
}

/** Whether a service's rows need a room from this group. */
export const needsRoomGroup = (rows, groupId) => !!groupId && (rows || []).some((r) => (r.resourceRequirements || []).some((q) => q && idOf(q.groupId) === idOf(groupId)));

const axiosReason = (e, fallback) => clean((e && e.response && e.response.data && e.response.data.error && e.response.data.error.responseMessage) || fallback);

/**
 * A "Treatment rooms" group with Room 1 to Room n, bookable whenever the
 * team is in. The group is only ever the one with that name (made if it's
 * missing, never another group). Rooms are matched by name, so running it
 * again adds nothing twice, and a room switched off in Resources is
 * switched back on rather than made again.
 */
export async function addRooms(count) {
  try {
    let { groups, units, inactive } = await loadRooms();
    let group = roomsGroupOf(groups);
    if (!group) {
      const r = await axios.post(API.RESOURCE_GROUP, { name: ROOMS_NAME }, { headers: jsonHeaders() });
      if (!ok(r)) throw new Error(reason(r, 'I couldn’t add your rooms.'));
      ({ groups, units, inactive } = await loadRooms());
      group = roomsGroupOf(groups);
      if (!group) throw new Error('I added your rooms but couldn’t find them again. Try once more.');
    }
    const have = new Set(units.filter((u) => u.groupId === group.id).map((u) => norm(u.name)));
    const off = inactive.filter((u) => u.groupId === group.id);
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].reduce((a, k) => ({ ...a, [k]: { open: k !== 'sun', start: 540, end: 1080 } }), {});
    for (let i = 1; i <= count; i += 1) {
      const name = `Room ${i}`;
      if (have.has(norm(name))) continue; // eslint-disable-line no-continue
      const old = off.find((u) => norm(u.name) === norm(name));
      if (old) {
        // Switched off in Resources: back on, with everything else as it was.
        const r = await axios.put(`${API.RESOURCE_UNIT}/${old.id}`, { isActive: true }, { headers: jsonHeaders() });
        if (!ok(r)) throw new Error(reason(r, `I couldn’t switch ${name} back on.`));
      } else {
        const fd = new FormData();
        fd.append('name', name);
        fd.append('turnoverTime', 0);
        fd.append('diaryColour', DEFAULT_COLOUR);
        fd.append('notes', '');
        fd.append('hours', JSON.stringify({ mode: 'always', days }));
        fd.append('groupId', group.id);
        fd.append('existingImages', '[]');
        // FormData sets its own Content-Type, with the boundary.
        const r = await axios.post(API.RESOURCE_UNIT, fd, { headers: { accessToken: localStorage.getItem('accessToken') } });
        if (!ok(r)) throw new Error(reason(r, `I couldn’t add ${name}.`));
      }
    }
    return loadRooms();
  } catch (e) {
    throw new Error(axiosReason(e, (e && e.message) || 'I couldn’t add your rooms.'));
  }
}
